import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Send, Phone, Mail, MapPin, MessageCircle, CheckCircle } from 'lucide-react';

const WHATSAPP_NUMBER = '5521998384393';
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}`;

const interests = [
  'Limpeza de Pele',
  'Micropigmentação Sobrancelha (Dermografo)',
  'Jato de Plasma',
  'Mini Tattoo',
  'Micropigmentação Labial',
  'Microblading Sobrancelha (Iniciante)',
  'Outro',
];

export const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Format message for WhatsApp
    const whatsappMessage = encodeURIComponent(
      `*Nova Mensagem do Site*\n\n` +
      `*Nome:* ${formData.name}\n` +
      `*Email:* ${formData.email}\n` +
      `*Telefone:* ${formData.phone}\n` +
      `*Interesse:* ${formData.interest}\n\n` +
      `*Mensagem:*\n${formData.message}`
    );

    // Open WhatsApp with pre-filled message
    setTimeout(() => {
      window.open(`${WHATSAPP_LINK}?text=${whatsappMessage}`, '_blank');
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Reset form after delay
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          interest: '',
          message: '',
        });
        setIsSubmitted(false);
      }, 3000);
    }, 1000);
  };

  return (
    <section
      id="contato"
      ref={ref}
      data-testid="contact-section"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-dark" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      
      {/* Decorative */}
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-gold font-medium tracking-wider uppercase text-sm mb-4">
            Contato
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Pronta para <span className="text-gold">Começar</span>?
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Entre em contato e dê o primeiro passo rumo à sua transformação profissional.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="font-heading text-2xl font-bold text-white mb-8">
              Informações de Contato
            </h3>

            <div className="space-y-6 mb-10">
              <motion.a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 glass-card rounded-xl hover:border-gold/50 transition-all group"
                whileHover={{ x: 10 }}
                data-testid="contact-whatsapp"
              >
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
                  <MessageCircle className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">WhatsApp</p>
                  <p className="text-white font-medium">(21) 99838-4393</p>
                </div>
              </motion.a>

              <motion.a
                href="mailto:contato@rithabarretoacademy.com"
                className="flex items-center gap-4 p-4 glass-card rounded-xl hover:border-gold/50 transition-all group"
                whileHover={{ x: 10 }}
                data-testid="contact-email"
              >
                <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center group-hover:bg-gold/30 transition-colors">
                  <Mail className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">E-mail</p>
                  <p className="text-white font-medium">contato@rithabarretoacademy.com</p>
                </div>
              </motion.a>

              <motion.a
                href="tel:+5521998384393"
                className="flex items-center gap-4 p-4 glass-card rounded-xl hover:border-gold/50 transition-all group"
                whileHover={{ x: 10 }}
                data-testid="contact-phone"
              >
                <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center group-hover:bg-gold/30 transition-colors">
                  <Phone className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Telefone</p>
                  <p className="text-white font-medium">(21) 99838-4393</p>
                </div>
              </motion.a>
            </div>

            {/* Quick WhatsApp CTA */}
            <motion.a
              href={`${WHATSAPP_LINK}?text=${encodeURIComponent('Olá, vim pelo site e quero saber sobre os cursos da Ritha Barreto Academy.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-green-500 text-white font-bold px-8 py-4 rounded-full hover:bg-green-600 transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="contact-whatsapp-cta"
            >
              <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Falar no WhatsApp Agora
            </motion.a>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="glass-card rounded-2xl p-8 lg:p-10">
              <h3 className="font-heading text-2xl font-bold text-white mb-6">
                Envie sua Mensagem
              </h3>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-20 h-20 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">Mensagem Enviada!</h4>
                  <p className="text-white/60">Você será redirecionada ao WhatsApp.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name */}
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Seu nome completo"
                      required
                      className="w-full bg-white/5 border-b border-white/20 focus:border-gold text-white placeholder:text-white/30 px-0 py-4 transition-all focus:bg-white/10 outline-none"
                      data-testid="contact-input-name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Seu e-mail"
                      required
                      className="w-full bg-white/5 border-b border-white/20 focus:border-gold text-white placeholder:text-white/30 px-0 py-4 transition-all focus:bg-white/10 outline-none"
                      data-testid="contact-input-email"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Seu WhatsApp"
                      required
                      className="w-full bg-white/5 border-b border-white/20 focus:border-gold text-white placeholder:text-white/30 px-0 py-4 transition-all focus:bg-white/10 outline-none"
                      data-testid="contact-input-phone"
                    />
                  </div>

                  {/* Interest */}
                  <div>
                    <select
                      name="interest"
                      value={formData.interest}
                      onChange={handleChange}
                      required
                      className="w-full bg-white/5 border-b border-white/20 focus:border-gold text-white px-0 py-4 transition-all focus:bg-white/10 outline-none appearance-none cursor-pointer"
                      style={{ color: formData.interest ? '#fff' : 'rgba(255,255,255,0.3)' }}
                      data-testid="contact-input-interest"
                    >
                      <option value="" disabled>Qual seu interesse?</option>
                      {interests.map((interest) => (
                        <option key={interest} value={interest} className="bg-dark-paper text-white">
                          {interest}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Sua mensagem (opcional)"
                      rows={4}
                      className="w-full bg-white/5 border-b border-white/20 focus:border-gold text-white placeholder:text-white/30 px-0 py-4 transition-all focus:bg-white/10 outline-none resize-none"
                      data-testid="contact-input-message"
                    />
                  </div>

                  {/* Submit */}
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gold text-black font-bold px-8 py-4 rounded-full hover:bg-gold-light transition-all btn-gold-glow flex items-center justify-center gap-2 disabled:opacity-70"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Enviar pelo WhatsApp
                      </>
                    )}
                  </motion.button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
